import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { client } from "@/lib/api-base";
import { useAuth } from "@/contexts/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { ModuleIcon } from "@/components/ModuleIcon";
import { SuiteLauncher } from "@/components/SuiteLauncher";
import {
  Home,
  CalendarClock,
  CalendarCheck2,
  RotateCcw,
  RotateCw,
  ReceiptText,
  HandCoins,
  TrendingUp,
  Scale,
  SettingsIcon,
  BarChart3,
  BookOpen,
  LogOut,
} from "lucide-react";

// Sidebar voices grouped by functional area. Each top-level array is
// a "cluster": clusters are visually separated by a divider. Within
// a cluster, items are tight; between clusters, extra vertical
// breathing room is added by the renderer.
const NAV_GROUPS = [
  // 1) Overview
  [
    { to: "/", label: "Cruscotto", icon: Home, testId: "nav-dashboard", end: true },
  ],
  // 2) Scadenzari (vista pianificata)
  [
    { to: "/occurrences", label: "Scadenzario Spese", icon: CalendarClock, testId: "nav-occurrences" },
    { to: "/receivables", label: "Scadenzario Incassi", icon: CalendarCheck2, testId: "nav-receivables",
      activeMatcher: (path) => {
        if (path === "/receivables") return true;
        if (!path.startsWith("/receivables/")) return false;
        return !(
          path.startsWith("/receivables/daily") ||
          path.startsWith("/receivables/recurring") ||
          path.startsWith("/receivables/ordinary")
        );
      },
    },
  ],
  // 3) Operatività (registrazioni: ricorrenti + ordinarie)
  [
    { to: "/expenses", label: "Spese ricorrenti", icon: RotateCw, testId: "nav-expenses" },
    { to: "/ordinary", label: "Spese ordinarie", icon: ReceiptText, testId: "nav-ordinary" },
    { to: "/receivables/recurring", label: "Incassi ricorrenti", icon: RotateCcw, testId: "nav-recurring-receivables",
      activeMatcher: (path) => path.startsWith("/receivables/recurring"),
    },
    { to: "/receivables/daily", label: "Incassi ordinari", icon: HandCoins, testId: "nav-daily-receipts",
      activeMatcher: (path) =>
        path.startsWith("/receivables/daily") ||
        path === "/receivables/ordinary",
    },
  ],
  // 4) Consuntivo
  [
    { to: "/prima-nota", label: "Prima Nota", icon: BookOpen, testId: "nav-prima-nota" },
  ],
  // 5) Analisi
  [
    { to: "/forecast", label: "Previsionale", icon: TrendingUp, testId: "nav-forecast" },
    { to: "/variance", label: "Scostamento", icon: Scale, testId: "nav-variance" },
    { to: "/analytics", label: "Report", icon: BarChart3, testId: "nav-analytics" },
  ],
  // 6) Setup
  [
    { to: "/settings", label: "Impostazioni", icon: SettingsIcon, testId: "nav-settings" },
  ],
];

export default function Layout() {
  // Custom studio logo: fetched once on mount and refreshed when the
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-background text-stone-900">
      <aside
        data-testid="main-sidebar"
        className="sticky top-0 flex h-screen w-[240px] shrink-0 flex-col border-r border-border bg-card"
      >
        <div className="px-4 pb-2 pt-4">
          <div className="flex items-center gap-2.5">
            <div
              data-testid="sidebar-brand-mark"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg shadow-sm ring-1 ring-primary/20"
              title="Gestionale Incassi & Pagamenti"
            >
              <ModuleIcon size={36} variant="solid" />
            </div>
            <div className="min-w-0 leading-tight">
              <div className="font-serif text-[12px] font-bold uppercase tracking-[0.08em] text-primary">
                Gruppo Impresa Suite
              </div>
              <div
                data-testid="sidebar-subtitle"
                className="mt-0.5 text-[10px] font-medium leading-tight text-stone-600"
              >
                Gestionale Incassi &amp; Pagamenti
              </div>
            </div>
          </div>
        </div>
        {/* Suite Launcher — unico punto ufficiale di passaggio tra i moduli */}
        <div className="px-3 pb-2">
          <SuiteLauncher compact />
        </div>
        <nav className="flex flex-1 flex-col px-3 py-2" data-testid="main-nav">
          {NAV_GROUPS.map((group, gIdx) => (
            <div
              key={`group-${gIdx}`}
              className="flex flex-col gap-0.5"
            >
              {group.map(({ to, label, icon: Icon, testId, end, disabled, activeMatcher }) => {
                if (disabled) {
                  return (
                    <span
                      key={testId}
                      data-testid={testId}
                      title="Modulo non ancora disponibile"
                      aria-disabled="true"
                      className="flex cursor-not-allowed items-center gap-2.5 rounded-md px-3 py-1.5 text-sm text-stone-300"
                    >
                      <Icon size={16} strokeWidth={1.8} />
                      <span>{label}</span>
                      <span className="ml-auto rounded-full bg-stone-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-stone-400">
                        Presto
                      </span>
                    </span>
                  );
                }
                const customActive = activeMatcher ? activeMatcher(location.pathname) : null;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={testId}
                    className={({ isActive }) => {
                      const active = customActive !== null ? customActive : isActive;
                      return cn(
                        "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-all duration-200",
                        active
                          ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary -ml-[2px] pl-[14px]"
                          : "text-stone-600 hover:bg-secondary hover:text-primary",
                      );
                    }}
                  >
                    <Icon size={16} strokeWidth={1.8} />
                    <span>{label}</span>
                  </NavLink>
                );
              })}
              {gIdx < NAV_GROUPS.length - 1 && (
                <div
                  data-testid={`nav-divider-${gIdx}`}
                  aria-hidden="true"
                  className="my-2 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent"
                />
              )}
            </div>
          ))}
        </nav>
        <div
          data-testid="sidebar-footer"
          className="mt-auto border-t border-stone-200 px-4 py-3 leading-tight text-stone-400"
        >
          <SidebarUserFooter />
          <div
            data-testid="corporate-brand"
            className="mt-2 flex items-center gap-2 px-1"
            title="Studio Bonaiuti — Gruppo Impresa STP a r.l."
          >
            <BrandMark size={16} />
            <div className="min-w-0 leading-tight">
              <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-600">
                Studio Bonaiuti
              </div>
              <div className="truncate text-[9px] text-stone-400">
                &amp; Gruppo Impresa STP a r.l.
              </div>
            </div>
          </div>
          <div className="mt-1.5 space-y-0.5 px-1 text-[10px]">
            <VersionBadge />
            <div>© Made by Gruppo Impresa Srl Stp</div>
          </div>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main className="min-w-0 flex-1 px-8 py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * TopBar v1.60.5 — barra superiore sempre visibile, sticky in alto.
 * Mostra utente autenticato + comando "Esci" in linea con gli altri
 * gestionali della Gruppo Impresa Suite. Nessun menu nascosto: il
 * bottone è raggiungibile con 1 click da qualsiasi pagina.
 *
 * Al click su Esci:
 *  - invalidazione token (client.post("/auth/logout") + clearToken locale)
 *  - status → "unauthenticated" → ProtectedShell redirige a /login
 *  - la username resta memorizzata in localStorage (v1.60.4)
 *  - il campo password nel form di login resta VUOTO
 *  - il back-button del browser NON riapre pagine protette: al primo
 *    render ProtectedShell verifica lo status e fa Navigate replace.
 */
function TopBar() {
  const { user, logout } = useAuth();
  const handleLogout = async () => {
    await logout();
    // Rimpiazza la history entry corrente: il tasto Indietro del browser
    // NON torna alla pagina protetta anche se il DOM è già smontato.
    try {
      window.history.replaceState(null, "", "/login");
    } catch {
      /* browser senza history API — fallback naturale via ProtectedShell */
    }
  };
  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-30 flex h-11 shrink-0 items-center justify-end gap-3 border-b border-stone-200 bg-white/85 px-6 backdrop-blur"
    >
      <span
        data-testid="topbar-username"
        className="hidden text-[12px] text-stone-500 sm:inline"
        title="Utente autenticato"
      >
        <span className="font-semibold text-stone-800">
          {user?.username || "—"}
        </span>
      </span>
      <span className="hidden text-stone-300 sm:inline" aria-hidden="true">
        |
      </span>
      <button
        type="button"
        data-testid="btn-topbar-logout"
        onClick={handleLogout}
        title="Esci dal gestionale"
        className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1 text-[12px] font-medium text-stone-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-100"
      >
        <LogOut size={13} strokeWidth={2} />
        Esci
      </button>
    </header>
  );
}

function SidebarUserFooter() {
  const { user } = useAuth();
  return (
    <div
      data-testid="sidebar-user"
      className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white p-2"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-100 text-[11px] font-semibold uppercase text-stone-600">
        {(user?.username || "?").slice(0, 1)}
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-[12px] font-semibold text-stone-800">
          {user?.username || "—"}
        </div>
        <div className="truncate text-[10px] text-stone-400">
          Sessione attiva
        </div>
      </div>
    </div>
  );
}

/**
 * VersionBadge — mostra la revisione corrente e la data ultimo
 * aggiornamento in modo discreto (foot-note sidebar).
 *
 * Legge da `GET /api/version` (endpoint pubblico). Silenzioso su
 * errore: se il fetch fallisce, il badge semplicemente non appare
 * (nessuna notifica intrusiva).
 */
function VersionBadge() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await client.get("/version");
        if (alive) setInfo(res.data);
      } catch {
        /* silent */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  if (!info?.version) return null;
  const dt = info.updated_at
    ? new Date(info.updated_at + "T00:00:00").toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";
  return (
    <div
      data-testid="version-badge"
      title={`Revisione ${info.version} · aggiornata al ${dt}`}
      className="flex items-center gap-1.5 tabular-nums text-stone-400"
    >
      <span className="font-mono">v{info.version}</span>
      {dt && (
        <>
          <span className="text-stone-300">·</span>
          <span>{dt}</span>
        </>
      )}
    </div>
  );
}
